import { message } from "antd";

import {
  buildFixedOrderFleet,
  dataStopTypeFixedOrder,
  deleteShiftStart,
  groupJobsByRouteKey
} from "./helper";
import { doFixedOrderRoute, doSortOrder } from "./store/thunks";
import { FIXED_ORDER_ENDPOINT_MODES, STOP_ORDER_MODES } from "./stopOrderModes";

/**
 * Builds routes from the user's own stop order instead of letting the engine choose one.
 *
 * Groups jobs into routes (by the import's "Prerouted Route ID" column when every stop has
 * one), builds the flat VRP-shaped visits map plus a fleet carrying each route's `order`
 * array, then sends it either to the routing engine's VRP path or to the OSRM-backed
 * endpoint. Shared by the client and admin confirm pages, which differ only in their
 * surrounding wiring.
 *
 * @returns the dispatched thunk's promise, or undefined for the engine-backed path.
 */
export const dispatchStopOrderMode = (
  dispatch,
  {
    stopOrderMode,
    newOrder,
    tmpJobs,
    draftId,
    userId,
    clientId,
    vehicleCategoryList,
    preferredNoOfStops,
    onError = () => null
  }
) => {
  const groups = groupJobsByRouteKey(tmpJobs, preferredNoOfStops);
  const visits = dataStopTypeFixedOrder(tmpJobs);
  const fleet = buildFixedOrderFleet(newOrder, groups);
  const order = { ...newOrder, routes: fleet };

  if (FIXED_ORDER_ENDPOINT_MODES.includes(stopOrderMode)) {
    return dispatch(
      doFixedOrderRoute({
        userId,
        draftId,
        mode: stopOrderMode,
        visits,
        fleet,
        options: { polylines: true },
        order,
        vehicleCategoryList,
        numberRoutes: Object.keys(groups).length,
        isSelectRouteNumber: true,
        tags: [`client_id_${clientId}`]
      })
    ).then((action) => {
      if (action?.error) {
        const content =
          action.error.message || "Could not build routes from your stop order.";
        onError(content);
        message.error({ content, duration: 10 });
        return;
      }
      if (!action?.payload?.degraded) return;
      // Never let a degraded result pass for the real thing — especially for osrm_optimize,
      // where the stops came back in exactly the order they went in.
      message.warning({
        content:
          stopOrderMode === STOP_ORDER_MODES.OSRM_OPTIMIZE
            ? "OSRM was unreachable, so the stops were NOT optimized — they kept the order you gave. Drive times and distances are unavailable."
            : "OSRM was unreachable. Your stop order was kept, but drive times, distances and map lines are unavailable.",
        duration: 10
      });
    });
  }

  // PREDEFINED_VRP: the engine honors fleet[].order by seeding it as a jsprit initial route.
  // max_iterations is passed explicitly because the proxy's auto-clamp only inspects the
  // last fleet entry and would miss a request mixing ordered and unordered vehicles.
  return dispatch(
    doSortOrder({
      userId,
      draftId,
      visits,
      fleet: deleteShiftStart(fleet),
      options: {
        polylines: true,
        max_iterations: 1,
        optimization_preference: "time"
      },
      order,
      vehicleCategoryList,
      tags: [`client_id_${clientId}`]
    })
  );
};
